/**
 * Messaging API - secure clinical messaging and formal email mailbox.
 */
import apiClient, { apiDelete, apiGet, apiGetList, apiPost, apiPut } from './client';
import type { Message, PaginatedResponse } from '@/types';
import type {
  MailAddress,
  MailAttachment,
  MailComposePayload,
  MailDraft,
  MailboxFolder,
  MailboxPage,
  MailMessage,
  MailThread,
  MailThreadSummary,
} from '@/pages/messages/mailbox.types';

export function listMessages(params?: {
  page?: number;
  per_page?: number;
  folder?: string;
}): Promise<PaginatedResponse<Message>> {
  const folder = params?.folder ?? 'inbox';
  const endpoint = folder === 'sent' ? '/messages/sent' : '/messages/inbox';
  return apiGetList<Message>(endpoint, {
    params: { page: params?.page, per_page: params?.per_page },
  });
}

export function getMessage(id: string): Promise<Message> {
  return apiGet<Message>(`/messages/${id}`);
}

export function sendMessage(data: Partial<Message>): Promise<Message> {
  return apiPost<Message>('/messages', data);
}

export function markAsRead(id: string): Promise<void> {
  return apiPut<void>(`/messages/${id}/read`);
}

export function getUnreadCount(): Promise<number> {
  return apiGet<{ unread: number }>('/messages/unread-count').then((r) => r.unread);
}

export function getFlaggedCount(): Promise<number> {
  return apiGet<{ flagged: number }>('/messages/flagged-count').then((r) => r.flagged);
}

export function listPatientSMS(
  patientId: string,
  page = 1,
  perPage = 50,
  signal?: AbortSignal,
): Promise<PaginatedResponse<Message>> {
  return apiGetList<Message>('/messages/sms', {
    params: { patient_id: patientId, page, per_page: perPage },
    signal,
  });
}

export function sendPatientSMS(patientId: string, body: string): Promise<Message> {
  return apiPost<Message>('/messages/sms', { patient_id: patientId, body });
}

export function getSMSConfiguration(): Promise<{ enabled: boolean }> {
  return apiGet<{ enabled: boolean }>('/messages/sms/config');
}

export interface MailboxListParams {
  folder: MailboxFolder;
  page?: number;
  per_page?: number;
  q?: string;
}

interface BackendRecipient {
  recipient_id?: string;
  recipient_type: 'user' | 'patient' | 'external';
  kind: 'from' | 'to' | 'cc' | 'bcc';
  display_name?: string;
  email_address: string;
}

interface BackendAttachment {
  id: string;
  file_name: string;
  mime_type: string;
  file_size: number;
}

interface BackendMailboxMessage extends Message {
  folder: string;
  is_read: boolean;
  is_starred: boolean;
  direction?: 'inbound' | 'outbound';
  recipients: BackendRecipient[];
  email_attachments: BackendAttachment[];
}

function mapAddress(recipient: BackendRecipient): MailAddress {
  return {
    id: recipient.recipient_id,
    kind: recipient.recipient_type,
    name: recipient.display_name,
    email: recipient.email_address,
    recipient_type: recipient.kind === 'from' ? undefined : recipient.kind,
  };
}

function mapAttachment(attachment: BackendAttachment): MailAttachment {
  return attachment;
}

function mapMailboxMessage(message: BackendMailboxMessage): MailMessage {
  const fromRecord = message.recipients.find((recipient) => recipient.kind === 'from');
  const outboundFallback: MailAddress = {
    id: message.sender_id,
    kind: message.sender_type === 'patient' ? 'patient' : 'user',
    name: 'Me',
    email: '',
  };
  return {
    id: message.id,
    thread_id: message.thread_id || message.id,
    subject: message.subject,
    body_text: message.body,
    from: fromRecord ? mapAddress(fromRecord) : outboundFallback,
    recipients: message.recipients
      .filter((recipient) => recipient.kind !== 'from')
      .map(mapAddress),
    attachments: (message.email_attachments ?? []).map(mapAttachment),
    direction: message.direction || 'outbound',
    status: message.status,
    sent_at: message.sent_at,
    received_at:
      message.direction === 'inbound' ? message.sent_at || message.created_at : undefined,
    created_at: message.created_at,
  };
}

function mapSummary(message: BackendMailboxMessage): MailThreadSummary {
  const mapped = mapMailboxMessage(message);
  const participants =
    mapped.direction === 'inbound'
      ? [mapped.from]
      : mapped.recipients.filter((recipient) => recipient.recipient_type !== 'bcc');
  return {
    id: mapped.thread_id,
    message_ids: [mapped.id],
    subject: mapped.subject,
    snippet: mapped.body_text.replace(/\s+/g, ' ').slice(0, 160),
    participants,
    latest_message_at: mapped.received_at || mapped.sent_at || mapped.created_at,
    message_count: 1,
    unread_count: message.is_read ? 0 : 1,
    is_starred: message.is_starred,
    has_attachments: mapped.attachments.length > 0,
    delivery_status: mapped.direction === 'outbound' ? mapped.status : undefined,
    draft_id: message.folder === 'drafts' ? message.id : undefined,
  };
}

/** Legacy secure messages (care coordination, portal) before mailbox mirroring. */
function mapLegacyMessage(message: Message, folder: 'inbox' | 'sent'): MailMessage {
  const inbound = folder === 'inbox';
  return {
    id: message.id,
    thread_id: message.thread_id || message.id,
    subject: message.subject,
    body_text: message.body,
    from: {
      id: message.sender_id,
      kind: message.sender_type === 'patient' ? 'patient' : 'user',
      name: message.sender_name || 'Sender',
      email: '',
    },
    recipients: [
      {
        id: message.recipient_id,
        kind: message.recipient_type === 'patient' ? 'patient' : 'user',
        name: message.recipient_name || 'Recipient',
        email: '',
        recipient_type: 'to',
      },
    ],
    attachments: [],
    direction: inbound ? 'inbound' : 'outbound',
    status: message.status,
    sent_at: message.sent_at || message.created_at,
    received_at: inbound ? message.sent_at || message.created_at : undefined,
    created_at: message.created_at,
  };
}

function mapLegacySummary(message: Message, folder: 'inbox' | 'sent'): MailThreadSummary {
  const mapped = mapLegacyMessage(message, folder);
  return {
    id: mapped.thread_id,
    message_ids: [mapped.id],
    subject: mapped.subject,
    snippet: mapped.body_text.replace(/\s+/g, ' ').slice(0, 160),
    participants: folder === 'inbox' ? [mapped.from] : mapped.recipients,
    latest_message_at: mapped.received_at || mapped.sent_at || mapped.created_at,
    message_count: 1,
    unread_count: message.status === 'sent' && folder === 'inbox' ? 1 : 0,
    is_starred: message.is_starred,
    has_attachments: false,
    delivery_status: folder === 'sent' ? message.status : undefined,
  };
}

async function mergeLegacySecureMessages(
  folder: MailboxFolder,
  grouped: Map<string, MailThreadSummary>,
  signal?: AbortSignal,
): Promise<void> {
  if (folder !== 'inbox' && folder !== 'sent') return;
  const legacyFolder = folder === 'sent' ? 'sent' : 'inbox';
  try {
    const legacy = await listMessages({ folder: legacyFolder, page: 1, per_page: 50 });
    signal?.throwIfAborted();
    const existingMessageIds = new Set(
      Array.from(grouped.values()).flatMap((summary) => summary.message_ids),
    );
    for (const message of legacy.data) {
      if (message.channel === 'chat' || message.channel === 'sms') continue;
      if (existingMessageIds.has(message.id)) continue;
      const summary = mapLegacySummary(message, legacyFolder);
      const existing = grouped.get(summary.id);
      if (!existing) {
        grouped.set(summary.id, summary);
        continue;
      }
      existing.message_count += 1;
      existing.message_ids.push(...summary.message_ids);
      existing.unread_count += summary.unread_count;
      existing.is_starred ||= summary.is_starred;
      if (summary.latest_message_at > existing.latest_message_at) {
        Object.assign(existing, {
          latest_message_at: summary.latest_message_at,
          snippet: summary.snippet,
          participants: summary.participants,
          delivery_status: summary.delivery_status,
        });
      }
    }
  } catch {
    // Legacy inbox is optional; mailbox remains usable if this fails.
  }
}

function backendRecipients(data: MailComposePayload): BackendRecipient[] {
  return [
    ...data.to.map((recipient) => ({ recipient, kind: 'to' as const })),
    ...data.cc.map((recipient) => ({ recipient, kind: 'cc' as const })),
    ...data.bcc.map((recipient) => ({ recipient, kind: 'bcc' as const })),
  ].map(({ recipient, kind }) => ({
    recipient_id: recipient.id,
    recipient_type: recipient.kind,
    kind,
    display_name: recipient.name,
    email_address: recipient.email,
  }));
}

function backendCompose(data: MailComposePayload) {
  return {
    recipients: backendRecipients(data),
    subject: data.subject,
    body: data.body_text,
    priority: 'normal',
  };
}

function mapDraft(message: BackendMailboxMessage): MailDraft {
  return {
    id: message.id,
    version: Math.max(1, Number((message as unknown as { version?: number }).version || 1)),
    thread_id: message.thread_id,
    to: message.recipients.filter((recipient) => recipient.kind === 'to').map(mapAddress),
    cc: message.recipients.filter((recipient) => recipient.kind === 'cc').map(mapAddress),
    bcc: message.recipients.filter((recipient) => recipient.kind === 'bcc').map(mapAddress),
    subject: message.subject,
    body_text: message.body,
    updated_at: message.updated_at,
    attachments: (message.email_attachments ?? []).map(mapAttachment),
  };
}

export async function listMailbox(
  params: MailboxListParams,
  signal?: AbortSignal,
): Promise<MailboxPage> {
  const backendFolder = params.folder === 'starred' ? 'inbox' : params.folder;
  const response = await apiGetList<BackendMailboxMessage>(
    `/messages/mailbox/${backendFolder}`,
    {
      params: {
        page: params.page,
        per_page: params.per_page,
        search: params.q,
        is_starred: params.folder === 'starred' ? true : undefined,
      },
      signal,
    },
  );
  const grouped = new Map<string, MailThreadSummary>();
  response.data.map(mapSummary).forEach((summary) => {
    const existing = grouped.get(summary.id);
    if (!existing) {
      grouped.set(summary.id, summary);
      return;
    }
    existing.message_count += 1;
    existing.message_ids.push(...summary.message_ids);
    existing.unread_count += summary.unread_count;
    existing.is_starred ||= summary.is_starred;
    existing.has_attachments ||= summary.has_attachments;
    if (summary.latest_message_at > existing.latest_message_at) {
      Object.assign(existing, {
        latest_message_at: summary.latest_message_at,
        snippet: summary.snippet,
        participants: summary.participants,
        delivery_status: summary.delivery_status,
      });
    }
  });
  await mergeLegacySecureMessages(params.folder, grouped, signal);
  const [unread, legacyUnread, drafts] = await Promise.all([
    apiGetList<BackendMailboxMessage>('/messages/mailbox/inbox', {
      params: { page: 1, per_page: 1, is_read: false },
      signal,
    })
      .then((result) => result.meta.total_items)
      .catch(() => 0),
    getUnreadCount().catch(() => 0),
    apiGetList<BackendMailboxMessage>('/messages/mailbox/drafts', {
      params: { page: 1, per_page: 1 },
      signal,
    })
      .then((result) => result.meta.total_items)
      .catch(() => 0),
  ]);
  signal?.throwIfAborted();
  const merged = Array.from(grouped.values()).sort(
    (a, b) => new Date(b.latest_message_at).getTime() - new Date(a.latest_message_at).getTime(),
  );
  return {
    data: merged,
    page: response.meta.page,
    per_page: response.meta.per_page,
    total: Math.max(response.meta.total_items, merged.length),
    total_pages: response.meta.total_pages,
    unread: Math.max(unread, legacyUnread),
    folder_counts: { drafts },
  };
}

export async function getMailThread(id: string): Promise<MailThread> {
  const legacy = await apiGet<Message[]>(`/messages/thread/${id}`);
  const messages: MailMessage[] = [];
  for (const message of legacy) {
    try {
      const mailboxMessage = await apiGet<BackendMailboxMessage>(
        `/messages/mailbox-message/${message.id}`,
      );
      messages.push(mapMailboxMessage(mailboxMessage));
    } catch {
      messages.push(mapLegacyMessage(message, 'inbox'));
    }
  }
  return {
    id,
    subject: messages[0]?.subject || legacy[0]?.subject || '(No subject)',
    messages,
  };
}

export async function createDraft(data: MailComposePayload): Promise<MailDraft> {
  const message = await apiPost<BackendMailboxMessage>(
    '/messages/drafts',
    backendCompose(data),
  );
  return mapDraft(message);
}

export async function getDraft(id: string): Promise<MailDraft> {
  const message = await apiGet<BackendMailboxMessage>(`/messages/mailbox-message/${id}`);
  return mapDraft(message);
}

export async function updateDraft(
  id: string,
  data: MailComposePayload & { version?: number },
): Promise<MailDraft> {
  const message = await apiPut<BackendMailboxMessage>(
    `/messages/drafts/${id}`,
    backendCompose(data),
  );
  return mapDraft(message);
}

export function deleteDraft(id: string): Promise<void> {
  return apiDelete<void>(`/messages/drafts/${id}`);
}

async function uploadDraftFiles(
  draftId: string,
  files: File[],
  onProgress?: (percent: number) => void,
): Promise<void> {
  for (let index = 0; index < files.length; index += 1) {
    const form = new FormData();
    form.append('file', files[index]!);
    await apiClient.post(`/messages/drafts/${draftId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (!event.total) return;
        const fileProgress = event.loaded / event.total;
        onProgress?.(Math.round(((index + fileProgress) / files.length) * 100));
      },
    });
  }
}

export async function sendMailboxEmail(
  data: MailComposePayload,
  files: File[] = [],
  onProgress?: (percent: number) => void,
): Promise<MailThread> {
  if (data.draft_id || files.length > 0) {
    const draft = data.draft_id
      ? await updateDraft(data.draft_id, data)
      : await createDraft(data);
    if (files.length > 0) {
      await uploadDraftFiles(draft.id, files, onProgress);
    }
    const sent = await apiPost<BackendMailboxMessage>(
      `/messages/drafts/${draft.id}/send`,
      {},
    );
    return getMailThread(sent.thread_id || sent.id);
  }
  const sent = await apiPost<BackendMailboxMessage>('/messages/compose', backendCompose(data));
  return getMailThread(sent.thread_id || sent.id);
}

export function updateMailboxThread(
  id: string,
  action: 'star' | 'unstar' | 'read' | 'unread' | 'archive' | 'trash' | 'restore',
): Promise<void> {
  return apiPut<void>(`/messages/${id}/mailbox-action/${action}`, {});
}

export function bulkUpdateMailbox(
  messageIds: string[],
  action: 'read' | 'unread' | 'star' | 'unstar' | 'archive' | 'trash' | 'restore',
): Promise<void> {
  return apiPut<void>('/messages/mailbox-actions', {
    message_ids: messageIds,
    action,
  });
}

export async function downloadMailAttachment(id: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(`/messages/attachments/${id}/file`, {
    responseType: 'blob',
  });
  return response.data;
}
